import { computed, reactive, type ComputedRef } from 'vue';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import type { ComputedBreakdown, FeeMethod, ExpenseArrangement } from '@/models/settlementTypes';

interface SettingsFormLike {
  feePercentage: number | null;
  feeMethod: FeeMethod | null;
  expenseArrangement: ExpenseArrangement | null;
  roundToNearestDollar: boolean;
}

/**
 * Computes every prop SettlementSummaryCard needs, so MatterSettlement.vue's two
 * call sites reduce to a single v-bind. All but the two parameters below are pure
 * settlementStore/meStore reads — mirrors the useDisbursements.ts composable in
 * this same directory. settingsForm and completedSteps stay parent-supplied
 * because they carry state the store alone doesn't hold: settingsForm is a local
 * draft that can diverge from the store (matter-default seeding, optimistic
 * pre-persist updates), and completedSteps merges in a locally-tracked ref.
 */
export function useSettlementSummaryCardProps(
  settingsForm: SettingsFormLike,
  completedSteps: ComputedRef<number[]>,
) {
  const settlementStore = useSettlementStore();
  const meStore = useMeStore();

  const breakdown = computed((): ComputedBreakdown | null => settlementStore.data?.computed ?? null);

  const lienTotal = computed(
    () => breakdown.value?.lienTotal ?? settlementStore.liens.totalLiens ?? 0,
  );

  // The backend estimates expenses/liens even before a deposit exists (sc-48633), but always
  // sends a number — so only a non-zero estimate counts, or the empty state would never show.
  const hasSummaryData = computed(
    () =>
      settlementStore.recoveryAmount > 0 ||
      settlementStore.data?.recoveryAmount != null ||
      settlementStore.data?.locked?.expenseTotal != null ||
      (breakdown.value?.totalExpenses ?? 0) > 0 ||
      lienTotal.value > 0,
  );

  const displayRecoveryAmount = computed(
    () => settlementStore.data?.recoveryAmount ?? settlementStore.recoveryAmount,
  );

  // reactive() (not a plain object literal) so each computed ref below is auto-unwrapped on
  // property access — required for `v-bind="summaryCardProps"` at the call site to hand
  // SettlementSummaryCard plain unwrapped values instead of raw ComputedRef objects.
  return reactive({
    hasSummaryData,
    displayRecoveryAmount,
    // Preserve null (not-yet-configured) distinctly from 0 (a valid pro-bono contingency) —
    // SettlementSummaryCard's own guards check for null, not falsy, specifically so 0% still
    // predicts/renders as a computable $0.00 fee rather than reading as unconfigured.
    feePercentage: computed(() => settingsForm.feePercentage),
    breakdown,
    lienTotal,
    completedSteps,
    feeMethod: computed(() => settingsForm.feeMethod),
    expenseArrangement: computed(() => settingsForm.expenseArrangement),
    roundToNearestDollar: computed(() => settingsForm.roundToNearestDollar),
    isFullyComplete: computed(() => settlementStore.isGenerated),
    generatedDate: computed(() => settlementStore.statement.generatedAt),
    redesignEnabled: computed(() => !!meStore.me?.features?.settlementRedesign),
    isExpensesConfirmed: computed(() => breakdown.value?.isExpensesConfirmed ?? false),
    isLiensConfirmed: computed(() => breakdown.value?.isLiensConfirmed ?? false),
    isAttorneyFeeConfirmed: computed(() => breakdown.value?.isAttorneyFeeConfirmed ?? false),
    isClientNetRecoveryConfirmed: computed(() => breakdown.value?.isClientNetRecoveryConfirmed ?? false),
  });
}
